import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

// Distance between two touch points
const touchDistance = (t1, t2) => {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.hypot(dx, dy);
};

const MIN_SCALE = 0.55;
const MAX_SCALE = 1.15;

export function HelpDialog({ open, onClose, showRememberOption = false }) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [scale, setScale] = useState(1);
  const [isGesturing, setIsGesturing] = useState(false);
  const gestureRef = useRef({
    mode: null, // 'drag' | 'pinch' | null
    startY: 0,
    startTranslateY: 0,
    startDistance: 0,
    startScale: 1,
  });

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Reset gesture state when dialog opens
  useEffect(() => {
    if (open) {
      setTranslateY(0);
      setScale(1);
    }
  }, [open]);

  const handleClose = () => {
    if (showRememberOption && dontShowAgain) {
      localStorage.setItem("sbk_help_dismissed", "true");
    }
    onClose();
  };

  const onTouchStart = useCallback((e) => {
    if (!isMobile) return;
    if (e.touches.length === 2) {
      gestureRef.current = {
        mode: "pinch",
        startY: 0,
        startTranslateY: translateY,
        startDistance: touchDistance(e.touches[0], e.touches[1]),
        startScale: scale,
      };
      setIsGesturing(true);
    } else if (e.touches.length === 1) {
      gestureRef.current = {
        mode: "drag",
        startY: e.touches[0].clientY,
        startTranslateY: translateY,
        startDistance: 0,
        startScale: scale,
      };
      setIsGesturing(true);
    }
  }, [isMobile, translateY, scale]);

  const onTouchMove = useCallback((e) => {
    if (!isMobile || !gestureRef.current.mode) return;
    if (gestureRef.current.mode === "pinch" && e.touches.length === 2) {
      const dist = touchDistance(e.touches[0], e.touches[1]);
      const ratio = dist / gestureRef.current.startDistance;
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, gestureRef.current.startScale * ratio));
      setScale(next);
      e.preventDefault();
    } else if (gestureRef.current.mode === "drag" && e.touches.length === 1) {
      const dy = e.touches[0].clientY - gestureRef.current.startY;
      // Clamp to a comfortable range so the card doesn't fly off-screen
      const next = Math.max(-260, Math.min(260, gestureRef.current.startTranslateY + dy));
      setTranslateY(next);
    }
  }, [isMobile]);

  const onTouchEnd = useCallback(() => {
    gestureRef.current.mode = null;
    setIsGesturing(false);
  }, []);

  const resetTransform = () => {
    setTranslateY(0);
    setScale(1);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={
          isMobile
            ? "max-w-[92vw] p-3 gap-2 rounded-2xl overflow-hidden"
            : "sm:max-w-lg"
        }
        style={
          isMobile
            ? {
                transform: `translate(-50%, calc(-50% + ${translateY}px)) scale(${scale})`,
                transformOrigin: "center center",
                transition: isGesturing ? "none" : "transform 0.25s ease-out",
                touchAction: "none",
              }
            : undefined
        }
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {isMobile && (
          <>
            <div className="w-10 h-1.5 bg-slate-300 rounded-full mx-auto mb-1" aria-hidden="true"></div>
            {(translateY !== 0 || scale !== 1) && (
              <button
                type="button"
                onClick={resetTransform}
                className="absolute left-3 top-3 text-[10px] font-semibold text-slate-500 hover:text-slate-800 uppercase tracking-wider bg-white/70 rounded-full px-2 py-0.5"
                data-testid="help-reset-transform"
              >
                Centrar
              </button>
            )}
          </>
        )}

        <DialogHeader className={isMobile ? "space-y-0.5" : undefined}>
          <DialogTitle className={isMobile ? "text-lg" : "text-2xl"}>
            Ayuda - SBK-MAPS
          </DialogTitle>
          <DialogDescription className={isMobile ? "text-xs" : undefined}>
            {isMobile ? "Arrastrá para mover · Pellizcá para achicar" : "Instrucciones de uso de la aplicación"}
          </DialogDescription>
        </DialogHeader>

        <div className={isMobile ? "space-y-3 py-2" : "space-y-6 py-4"}>
          <div className="flex items-start gap-2 md:gap-3">
            <div className={`flex-shrink-0 rounded-full bg-[#FF5A5F] text-white flex items-center justify-center font-bold ${isMobile ? "w-6 h-6 text-xs" : "w-8 h-8"}`}>
              1
            </div>
            <div>
              <h3 className={`font-semibold mb-0.5 ${isMobile ? "text-sm" : "text-lg mb-1"}`}>
                Cargar puntos en el mapa
              </h3>
              <p className={`text-slate-600 leading-relaxed ${isMobile ? "text-xs" : "text-sm"}`}>
                Haz <strong>doble clic</strong> en cualquier lugar del mapa para agregar un nuevo punto.
                Completa el nombre, descripción y link de Instagram. Agrega tu punto, el mismo quedará en espera hasta que sea aprobado por el administrador.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2 md:gap-3">
            <div className={`flex-shrink-0 rounded-full bg-[#FF5A5F] text-white flex items-center justify-center font-bold ${isMobile ? "w-6 h-6 text-xs" : "w-8 h-8"}`}>
              2
            </div>
            <div>
              <h3 className={`font-semibold mb-0.5 ${isMobile ? "text-sm" : "text-lg mb-1"}`}>
                Iniciar sesión
              </h3>
              <p className={`text-slate-600 leading-relaxed ${isMobile ? "text-xs" : "text-sm"}`}>
                Haz clic en el botón <strong>"Iniciar sesión"</strong> del panel e ingresa tu usuario y contraseña.
                Solo usuarios autenticados pueden editar y eliminar puntos existentes. Para ser usuario, comunicarse con el administrador.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2 md:gap-3">
            <div className={`flex-shrink-0 rounded-full bg-[#FF5A5F] text-white flex items-center justify-center font-bold ${isMobile ? "w-6 h-6 text-xs" : "w-8 h-8"}`}>
              3
            </div>
            <div>
              <h3 className={`font-semibold mb-0.5 ${isMobile ? "text-sm" : "text-lg mb-1"}`}>
                Buscar un lugar
              </h3>
              <p className={`text-slate-600 leading-relaxed ${isMobile ? "text-xs" : "text-sm"}`}>
                Usa la <strong>barra de búsqueda</strong> en la parte superior del mapa para buscar direcciones y lugares.
                El mapa se moverá automáticamente a la ubicación encontrada fijando un punto verde temporal. Ejemplo: <em>"av argentina 50, neuquen"</em>.
              </p>
            </div>
          </div>
        </div>

        <div className={`flex items-center justify-between gap-3 ${isMobile ? "pt-1" : "pt-2 gap-4"}`}>
          {showRememberOption ? (
            <div className="flex items-center gap-2">
              <Checkbox
                id="dont-show-again"
                data-testid="dont-show-again-checkbox"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
              />
              <Label
                htmlFor="dont-show-again"
                className={`cursor-pointer select-none ${isMobile ? "text-xs text-slate-600" : "text-sm text-slate-600"}`}
              >
                No mostrar de nuevo
              </Label>
            </div>
          ) : (
            <div />
          )}
          <Button
            data-testid="help-understood-btn"
            onClick={handleClose}
            size={isMobile ? "sm" : "default"}
            className="bg-[#FF5A5F] hover:bg-[#E0484D] text-white rounded-full px-5"
          >
            Entendido
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
